/**
 * InlineClauseFlagPopover — floating clause-review card.
 *
 * Used as the landing surface for the IssuesPanel "Open in doc" click
 * so the user can inspect and triage one flag without entering global
 * review mode (which would swap the chat column for ReviewPanel cards,
 * replace the doc pagination bar with ReviewModeBar, AND open the
 * SourcePreviewDrawer overlay all at once).
 *
 * Behavior:
 * - Renders at the App root with `position: fixed` and is anchored to
 *   the top-right of the viewport just below the topbar by default.
 * - The header is a drag handle so the user can move the card to a
 *   spot that doesn't cover the part of the document they're reading.
 *   Position is clamped to keep at least a sliver of the popover
 *   on-screen so it can always be retrieved.
 * - Closes via the × button or Escape.
 *
 * Visual design notes (see corresponding prompt-side legal-tone style
 * guide in `backend/src/prompts/clauseFlaggingPrompt.ts`):
 * - Severity is signalled three ways simultaneously: a left accent
 *   bar, an icon (warning triangle for critical/high, info circle for
 *   medium/low/info), and a colored uppercase label. This is
 *   redundant on purpose so high-severity flags can never be visually
 *   confused with informational ones.
 * - Body sections are titled with legal labels ("Issue", "Risk
 *   basis", "Why this matters", "Recommended language", "Jurisdiction
 *   note") and individually iconified so the lawyer can scan to the
 *   section they care about at a glance.
 * - A separate "Attorney review recommended" badge surfaces the
 *   `requiresHumanReview` flag set by the LLM whenever confidence is
 *   low, state law is uncertain, or complex interpretation is
 *   involved.
 * - Confidence is shown as a small chip near the clause type so the
 *   reader knows how much weight to put on the suggestion.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ClauseFlag,
  ClauseFlagConfidence,
  ClauseFlagReviewBasis,
  ClauseFlagSeverity,
} from '@corner/shared';
import { APP } from '../tokens';

const POPOVER_WIDTH = 400;
const DEFAULT_TOP = 80;
const DEFAULT_RIGHT_MARGIN = 24;
/** Minimum visible portion when clamping so the popover can't be dragged fully off-screen. */
const MIN_VISIBLE = 60;

const SEVERITY_LABEL: Record<ClauseFlagSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

const SEVERITY_COLOR: Record<ClauseFlagSeverity, string> = {
  critical: '#C04A2A',
  high: '#D97645',
  medium: '#9A8B7A',
  low: '#A89E91',
  info: '#B7AFA4',
};

const SEVERITY_TINT: Record<ClauseFlagSeverity, string> = {
  critical: 'color-mix(in srgb, #C04A2A 12%, transparent)',
  high: 'color-mix(in srgb, #D97645 10%, transparent)',
  medium: 'color-mix(in srgb, #9A8B7A 8%, transparent)',
  low: 'transparent',
  info: 'transparent',
};

const REVIEW_BASIS_LABEL: Record<ClauseFlagReviewBasis, string> = {
  playbook_deviation: 'Playbook deviation',
  missing_clause: 'Missing clause',
  state_law_concern: 'State-law concern',
  one_sided_risk: 'One-sided risk',
  ambiguity: 'Ambiguity',
  business_risk: 'Business risk',
};

const CONFIDENCE_LABEL: Record<ClauseFlagConfidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

const CONFIDENCE_COLOR: Record<ClauseFlagConfidence, string> = {
  high: '#3F6D4E',
  medium: '#8B7335',
  low: '#9A6A4A',
};

export interface InlineClauseFlagPopoverProps {
  flag: ClauseFlag;
  /** Page on which the located evidence quote appears (PDF page hint). */
  page: number | null;
  /** Whether an action (accept/dismiss) is currently in flight. */
  busy: boolean;
  onAccept: () => void;
  onDismiss: () => void;
  /** Re-jump to the located clause (useful if the user scrolled away). */
  onLocate: () => void;
  /** Close the popover without changing flag status. */
  onClose: () => void;
}

export default function InlineClauseFlagPopover({
  flag,
  page,
  busy,
  onAccept,
  onDismiss,
  onLocate,
  onClose,
}: InlineClauseFlagPopoverProps) {
  const severityColor = SEVERITY_COLOR[flag.severity];
  const severityTint = SEVERITY_TINT[flag.severity];
  const isHighSeverity = flag.severity === 'critical' || flag.severity === 'high';

  // ── Drag state ────────────────────────────────────────────────────
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const clamp = useCallback((top: number, left: number) => {
    const width = popoverRef.current?.offsetWidth ?? POPOVER_WIDTH;
    const maxLeft = window.innerWidth - MIN_VISIBLE;
    const maxTop = window.innerHeight - MIN_VISIBLE;
    const minLeft = MIN_VISIBLE - width;
    const minTop = 0;
    return {
      top: Math.max(minTop, Math.min(maxTop, top)),
      left: Math.max(minLeft, Math.min(maxLeft, left)),
    };
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragOffsetRef.current) return;
      setPosition(clamp(
        event.clientY - dragOffsetRef.current.dy,
        event.clientX - dragOffsetRef.current.dx,
      ));
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (!dragOffsetRef.current || event.touches.length === 0) return;
      const touch = event.touches[0];
      setPosition(clamp(
        touch.clientY - dragOffsetRef.current.dy,
        touch.clientX - dragOffsetRef.current.dx,
      ));
      event.preventDefault();
    };
    const stop = () => {
      setDragging(false);
      dragOffsetRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', stop);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stop);
    };
  }, [dragging, clamp]);

  useEffect(() => {
    if (!position) return;
    const handleResize = () => {
      setPosition((prev) => (prev ? clamp(prev.top, prev.left) : prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, clamp]);

  const startDrag = useCallback((clientX: number, clientY: number) => {
    const rect = popoverRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = {
      dx: clientX - rect.left,
      dy: clientY - rect.top,
    };
    setPosition({ top: rect.top, left: rect.left });
    setDragging(true);
  }, []);

  const handleHeaderMouseDown = useCallback((event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    startDrag(event.clientX, event.clientY);
  }, [startDrag]);

  const handleHeaderTouchStart = useCallback((event: React.TouchEvent) => {
    if ((event.target as HTMLElement).closest('button')) return;
    if (event.touches.length === 0) return;
    const touch = event.touches[0];
    startDrag(touch.clientX, touch.clientY);
  }, [startDrag]);

  const positionStyle: React.CSSProperties = position
    ? { top: position.top, left: position.left }
    : { top: DEFAULT_TOP, right: DEFAULT_RIGHT_MARGIN };

  // Humanize the LLM-supplied clauseType ("non_compete" -> "Non compete").
  const clauseTypeLabel = humanizeClauseType(flag.clauseType);
  const reviewBasisLabel = REVIEW_BASIS_LABEL[flag.reviewBasis] ?? flag.reviewBasis;
  const confidenceLabel = CONFIDENCE_LABEL[flag.confidence] ?? flag.confidence;
  const confidenceColor = CONFIDENCE_COLOR[flag.confidence] ?? APP.textMuted;

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`Clause flag: ${flag.issue || 'Untitled flag'}`}
      style={{
        position: 'fixed',
        ...positionStyle,
        width: POPOVER_WIDTH,
        maxHeight: 'calc(100vh - 120px)',
        backgroundColor: APP.bgContent,
        border: `1px solid ${APP.border}`,
        borderRadius: 8,
        boxShadow: dragging
          ? '0 12px 32px rgba(0, 0, 0, 0.24)'
          : '0 8px 24px rgba(0, 0, 0, 0.18)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1200,
        fontFamily: APP.fontFamily,
        userSelect: dragging ? 'none' : 'auto',
        transition: dragging ? 'none' : 'box-shadow 120ms ease',
      }}
    >
      {/* Severity-colored accent bar at the top of the card. Doubles
          as a third visual signal for severity (in addition to icon
          and label) so the user can spot critical flags at a glance. */}
      <div
        style={{
          height: 3,
          backgroundColor: severityColor,
          flexShrink: 0,
        }}
      />

      <header
        onMouseDown={handleHeaderMouseDown}
        onTouchStart={handleHeaderTouchStart}
        title="Drag to reposition"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 14px 10px',
          borderBottom: `1px solid ${APP.border}`,
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          backgroundColor: severityTint,
        }}
      >
        <DragHandleIcon color={APP.textMuted} />
        <SeverityIcon severity={flag.severity} color={severityColor} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: severityColor,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {SEVERITY_LABEL[flag.severity]}
            </span>
            {clauseTypeLabel && (
              <Chip color={APP.textMuted} borderColor={APP.border}>
                {clauseTypeLabel}
              </Chip>
            )}
            <Chip color={confidenceColor} borderColor={APP.border} title={flag.confidenceReason ?? undefined}>
              {confidenceLabel}
            </Chip>
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: APP.textPrimary,
              marginTop: 4,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {flag.issue || 'Untitled flag'}
          </div>
          {(flag.sectionRef || page != null) && (
            <div
              style={{
                fontSize: 11,
                color: APP.textMuted,
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <SectionRefIcon color={APP.textMuted} />
              <span>
                {flag.sectionRef}
                {flag.sectionRef && page != null ? ' · ' : ''}
                {page != null ? `p.${page}` : ''}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: APP.textMuted,
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </header>

      {flag.requiresHumanReview && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            backgroundColor: 'color-mix(in srgb, #C04A2A 8%, transparent)',
            borderBottom: `1px solid ${APP.border}`,
            fontSize: 11,
            fontWeight: 500,
            color: '#C04A2A',
          }}
        >
          <AttorneyIcon color="#C04A2A" />
          <span>Attorney review recommended before acting on this flag.</span>
        </div>
      )}

      <div
        style={{
          padding: '12px 14px',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <Section
          icon={<ScaleIcon color={APP.textMuted} />}
          label="Risk basis"
        >
          <p style={paragraphStyle(APP.textPrimary)}>
            {reviewBasisLabel}
            {flag.confidenceReason && flag.confidence === 'low' && (
              <>
                {' · '}
                <span style={{ color: APP.textMuted, fontStyle: 'italic' }}>
                  {flag.confidenceReason}
                </span>
              </>
            )}
          </p>
        </Section>

        {flag.evidenceQuote && (
          <Section
            icon={<QuoteIcon color={APP.textMuted} />}
            label="Evidence from document"
          >
            <p
              style={{
                ...paragraphStyle(APP.textSecondary),
                fontStyle: 'italic',
                borderLeft: `2px solid ${APP.border}`,
                paddingLeft: 10,
              }}
            >
              “{flag.evidenceQuote}”
            </p>
          </Section>
        )}

        {flag.rationale && (
          <Section
            icon={<BookIcon color={APP.textMuted} />}
            label="Why this matters"
          >
            <p style={paragraphStyle(APP.textPrimary)}>{flag.rationale}</p>
          </Section>
        )}

        {flag.suggestedFallback && (
          <Section
            icon={<EditIcon color={APP.textMuted} />}
            label="Recommended language"
          >
            <p
              style={{
                ...paragraphStyle(APP.textPrimary),
                backgroundColor: 'var(--app-bg-history-active)',
                padding: '8px 10px',
                borderRadius: 4,
                border: `1px solid ${APP.border}`,
              }}
            >
              {flag.suggestedFallback}
            </p>
          </Section>
        )}

        {flag.jurisdictionNote && (
          <Section
            icon={<GavelIcon color={APP.textMuted} />}
            label="Jurisdictional consideration"
          >
            <p style={paragraphStyle(APP.textSecondary)}>{flag.jurisdictionNote}</p>
          </Section>
        )}

        {flag.tags && flag.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: -2 }}>
            {flag.tags.map((tag) => (
              <Chip key={tag} color={APP.textMuted} borderColor={APP.border}>
                {tag}
              </Chip>
            ))}
          </div>
        )}
      </div>

      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '10px 14px',
          borderTop: `1px solid ${APP.border}`,
          backgroundColor: APP.bgContent,
        }}
      >
        <button
          type="button"
          onClick={onLocate}
          disabled={busy}
          style={buttonStyleSecondary(busy)}
          title="Re-jump to this clause in the document"
        >
          Locate
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          style={buttonStyleSecondary(busy)}
          title="Mark this flag as dismissed without changes"
        >
          Dismiss
        </button>
        <button
          type="button"
          onClick={onAccept}
          disabled={busy}
          style={buttonStylePrimary(busy, isHighSeverity)}
          title="Mark this flag as accepted (will follow recommendation)"
        >
          Accept
        </button>
      </footer>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────

function Section({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon}
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: APP.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

function Chip({
  children,
  color,
  borderColor,
  title,
}: {
  children: React.ReactNode;
  color: string;
  borderColor: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        fontSize: 10,
        fontWeight: 500,
        color,
        border: `1px solid ${borderColor}`,
        borderRadius: 999,
        padding: '1px 7px',
        whiteSpace: 'nowrap',
        backgroundColor: APP.bgContent,
      }}
    >
      {children}
    </span>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────

function SeverityIcon({ severity, color }: { severity: ClauseFlagSeverity; color: string }) {
  const isWarning = severity === 'critical' || severity === 'high';
  if (isWarning) {
    return (
      <svg
        aria-hidden
        width={18}
        height={18}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, marginTop: 2 }}
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg
      aria-hidden
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, marginTop: 2 }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function AttorneyIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M3 21h18" />
      <path d="M5 21V10l7-5 7 5v11" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
}

function ScaleIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 3v18" />
      <path d="M5 8h14" />
      <path d="M5 8l-3 6h6z" />
      <path d="M19 8l3 6h-6z" />
    </svg>
  );
}

function QuoteIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill={color}
      style={{ flexShrink: 0 }}
    >
      <path d="M7 7h4v4H7c0 2.21 1.79 4 4 4v2c-3.31 0-6-2.69-6-6V7zm10 0h-4v4h4c0 2.21-1.79 4-4 4v2c3.31 0 6-2.69 6-6V7z" />
    </svg>
  );
}

function BookIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
  );
}

function EditIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function GavelIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M14 4l6 6" />
      <path d="M16 2l6 6-4 4-6-6 4-4z" />
      <path d="M9 11l5 5-7 7-5-5 7-7z" />
      <path d="M3 22h12" />
    </svg>
  );
}

function SectionRefIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={10}
      height={10}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function DragHandleIcon({ color }: { color: string }) {
  return (
    <svg
      aria-hidden
      width={10}
      height={16}
      viewBox="0 0 10 16"
      style={{ flexShrink: 0, marginTop: 4 }}
    >
      <circle cx={2} cy={3} r={1.2} fill={color} />
      <circle cx={2} cy={8} r={1.2} fill={color} />
      <circle cx={2} cy={13} r={1.2} fill={color} />
      <circle cx={8} cy={3} r={1.2} fill={color} />
      <circle cx={8} cy={8} r={1.2} fill={color} />
      <circle cx={8} cy={13} r={1.2} fill={color} />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function humanizeClauseType(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function paragraphStyle(color: string): React.CSSProperties {
  return {
    margin: 0,
    fontSize: 12,
    color,
    lineHeight: 1.5,
    wordBreak: 'break-word',
  };
}

function buttonStyleSecondary(busy: boolean): React.CSSProperties {
  return {
    fontFamily: APP.fontFamily,
    fontSize: 12,
    fontWeight: 500,
    padding: '6px 12px',
    border: `1px solid ${APP.border}`,
    borderRadius: 5,
    backgroundColor: APP.bgContent,
    color: APP.textPrimary,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
}

function buttonStylePrimary(busy: boolean, isHighSeverity: boolean): React.CSSProperties {
  // For high-severity flags we use a more cautious accept color so a
  // careless click feels heavier — matching the legal-tone copy in
  // the prompt that emphasizes attorney review for these flags.
  const bg = isHighSeverity ? '#7A3220' : '#1F4D2E';
  const border = isHighSeverity ? '#7A3220' : '#1F4D2E';
  return {
    fontFamily: APP.fontFamily,
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 14px',
    border: `1px solid ${border}`,
    borderRadius: 5,
    backgroundColor: bg,
    color: '#FFFFFF',
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.7 : 1,
    whiteSpace: 'nowrap',
  };
}
